import React, { useState } from 'react';
import { StudentSummary } from '../../types';
import { api } from '../../utils/api';

interface InviteParentModalProps {
  student: StudentSummary;
  onClose: () => void;
  onInviteSent: () => void;
}

const InviteParentModal: React.FC<InviteParentModalProps> = ({ student, onClose, onInviteSent }) => {
  const [parentEmail, setParentEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await api.post<{ invitation_token: string }>('/api/v1/parent/invite', {
        student_user_id: student.user_id,
        parent_email: parentEmail
      });

      setInviteLink(`${window.location.origin}/?invitation_token=${result.invitation_token}`);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Invite Parent for {student.first_name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl font-bold">&times;</button>
        </div>
        
        {inviteLink ? (
            <div className="text-center p-4 bg-green-100 text-green-800 rounded-md">
                <h3 className="font-bold mb-2">Invitation Created!</h3>
                <p className="text-sm mb-3">This app doesn't send emails automatically. Copy this link and share it with the parent yourself (email, text, etc.):</p>
                <div className="flex gap-2">
                    <input type="text" readOnly value={inviteLink} className="flex-grow p-2 border border-gray-300 rounded-md text-xs font-mono bg-white text-gray-800" onClick={(e) => (e.target as HTMLInputElement).select()} />
                    <button onClick={handleCopyLink} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm whitespace-nowrap">{copied ? 'Copied!' : 'Copy'}</button>
                </div>
                <button onClick={onInviteSent} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Done</button>
            </div>
        ) : (
            <form onSubmit={handleSubmit}>
                {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                <p className="text-gray-600 mb-4">
                    Enter the parent's email address below, then share the invitation link with them yourself (this app doesn't send emails automatically) so they can create an account and view {student.first_name}'s progress.
                </p>
                <div className="mb-4">
                    <label htmlFor="parent-email" className="block text-sm font-medium text-gray-700">Parent's Email</label>
                    <input
                        type="email"
                        id="parent-email"
                        value={parentEmail}
                        onChange={(e) => setParentEmail(e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        placeholder="parent@email.com"
                        required
                    />
                </div>
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Invitation'}
                    </button>
                </div>
            </form>
        )}
      </div>
    </div>
  );
};

export default InviteParentModal;